import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import practiceClient from '../client/PracticeClient';

/**
 * Сводка практик колоды для страницы выбора практики: что уже начато и как лежит по
 * боксам. Только чтение — init остаётся на кнопке «начать». Раньше страница получала
 * эти цифры через init по разу на формат, и каждый её показ создавал практики и
 * сохранял их документы.
 *
 * Одна пара (колода, тип) — один запрос; ответ отменённого запроса игнорируется, иначе
 * при быстрой смене колоды он мог бы перезаписать более поздний (тот же приём, что в
 * usePagedList). Загруженная сводка хранится вместе с ключом и отдаётся только пока
 * ключ совпадает с текущим: сводка прошлой колоды не должна пережить смену колоды,
 * даже если запрос по новой упал. `errorMessage` — уже переведённая строка, а не
 * функция перевода: строка сравнивается по значению и не перезапускает загрузку на
 * каждом рендере.
 */
const useDeckPracticeSummary = (deckId, practiceType, errorMessage) => {
    const [loaded, setLoaded] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [reloadCount, setReloadCount] = useState(0);

    useEffect(() => {
        if (!deckId) {
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            try {
                const response = await practiceClient.getDeckPracticeSummary(deckId, practiceType);
                if (!cancelled) {
                    setLoaded({ deckId, practiceType, summary: response });
                }
            } catch (error) {
                if (cancelled) {
                    return;
                }
                console.error('Error loading practice summary:', error);
                message.error(errorMessage);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [deckId, practiceType, errorMessage, reloadCount]);

    const reload = useCallback(() => setReloadCount((count) => count + 1), []);

    // Повторная загрузка той же колоды сводку не прячет; смена колоды — прячет сразу.
    const summary = loaded && loaded.deckId === deckId && loaded.practiceType === practiceType
        ? loaded.summary
        : null;

    /**
     * Строка сводки по формату; null — пользователь этот формат ещё не запускал.
     *
     * Документ практики опознаётся тройкой, и методика входит в неё: у одного формата их
     * столько, сколькими методиками его открывали. Строке нужно одно — когда этот формат
     * проходили в последний раз, — и это «последний раз» относится к формату, а не к
     * методике: переключение методики не должно выглядеть как «ни разу не проходили».
     * Поэтому берётся самая свежая строка среди методик этого формата.
     */
    const practiceFor = useCallback((practiceFormat, learningMethod) => {
        const rows = summary?.practices.filter(
            (practice) => practice.practiceFormat === practiceFormat) ?? [];
        const exact = rows.find((practice) => practice.learningMethod === learningMethod) ?? null;
        if (rows.length === 0) {
            return exact;
        }
        const newest = rows.reduce((latest, practice) =>
            new Date(practice.updatedAt ?? 0) > new Date(latest.updatedAt ?? 0) ? practice : latest);
        return { ...(exact ?? newest), updatedAt: newest.updatedAt };
    }, [summary]);

    return { summary, isLoading, reload, practiceFor };
};

export default useDeckPracticeSummary;
